import { useContext, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import useApi from 'hooks/useApi'
import { WppBreadcrumb, WppButton, WppTypography } from '@wppopen/components-library-react'
import Banner from 'components/Banner'
import NotFound404Page from 'pages/NotFound404Page/NotFound404Page'
import SettingsPageContext from 'context/settingsPageContext/settingsPageContext'
import appContext from 'context/appContext'
import { createCsApiUrlFromEndpoint, getFormattedDate } from 'utils'
import FormSkeleton from 'components/Skeletons/FormSkeleton'
import usePermissions from 'hooks/usePermissions'
import clsx from 'clsx'
import PageHeader from 'components/PageHeader/PageHeader'
import useToast from 'hooks/useToast'
import VersionHistoryList from 'components/VersionHistoryList/VersionHistoryList'
import { BANNER_KEYS, FILTER_IDS, PDP_GENERATOR_API_HEADERS, unknown } from 'utils/constants'
import { GenericModal } from 'components/Modals'
import { CreateAndEditNewBrandForm } from 'components/Forms'
import type {
  BreadcrumbItemEventDetails,
  WppBreadcrumbCustomEvent,
} from '@wppopen/components-library/dist/types/components'
import type {
  ArchiveCreateData,
  ArchiveCreatePayload,
  BrandsCreatePayload,
  BrandsDetailData,
  BrandsUpdateData,
  RestoreCreateBody,
  RestoreCreateData,
} from '@contracts'
import navigationHeaderStyles from '../../NavigationHeader.module.scss'
import styles from './Brands.module.scss'
import { OnSubmitGenericModalProps } from 'components/Modals/GenericModal/helper'
import useCurrentUser from 'hooks/useCurrentUser'
import { FEApiError } from 'utils/errorHandling'

export default function BrandView() {
  const navigate = useNavigate()
  const location = useLocation()
  const { setActiveBannerId, setToastDetailsContext, toastDetailsContext, resetBanner, activeBannerId } =
    useContext(SettingsPageContext)
  const {
    updatePreservedFilterSelection,
    preservedFilterSelection: currentFilterSelection,
    clientsData,
    fetchClientData,
    isClientApiLoading,
  } = useContext(appContext)
  const { client_id, brand_id } = useParams()
  const client = clientsData?.clients?.find(client => client.id === client_id)
  const [searchParams, setSearchParams] = useSearchParams()
  const [isFormActive, setIsFormActive] = useState(false)
  const { toastDetails, setToastDetails } = useToast(true)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false)
  const modalOpenState = useState<boolean>(false)
  const [isRestoreModalOpen, setIsRestoreModalOpen] = modalOpenState
  const singleBrandEndpoint = createCsApiUrlFromEndpoint(`brands/${brand_id}`)
  const { userName } = useCurrentUser()

  const {
    fetchData: getBrand,
    data: getBrandData,
    isLoading: isGetBrandLoading,
    url: getBrandUrl,
    error: getBrandError,
  } = useApi<BrandsDetailData>(singleBrandEndpoint, {}, 0, 'json', true)

  const {
    fetchData: putBrand,
    isLoading: isPutBrandLoading,
    url: putBrandUrl,
  } = useApi<BrandsDetailData>(singleBrandEndpoint, { method: 'PUT' }, 0, 'json', true)

  const {
    fetchData: deleteBrand,
    isLoading: isDeleteBrandLoading,
    url: deleteBrandUrl,
  } = useApi<ArchiveCreateData>(`brands/${brand_id}/archive`)

  const { clientLevelPermissions, isGetPermissionsLoading } = usePermissions({
    clientId: client_id,
    permissionSubset: [
      'PDP_APP_GET_BRANDS_BY',
      'PDP_APP_GET_BRANDS_BY_VERSION_BY',
      'PDP_APP_PUT_BRANDS_BY',
      'PDP_APP_POST_BRANDS_BY_VERSION_BY',
      'PDP_APP_POST_BRANDS_BY_ARCHIVE',
    ],
  })

  const [canGetBrand, canGetBrandVersion, canPutBrand, canPostRestoreBrand, canDeleteBrand] = clientLevelPermissions

  const breadCrumbItems = useMemo(
    () => [
      {
        label: 'Settings',
        path: '/settings',
      },
      {
        label: client?.client_name || 'client',
        path: `/settings/client/${client_id}`,
      },
      {
        label: getBrandData?.brand_name || 'brand',
        path: `/brands/${client_id}`,
      },
    ],
    [getBrandData?.brand_name, client_id, client?.client_name],
  )

  const handleRouteChange = (e: WppBreadcrumbCustomEvent<BreadcrumbItemEventDetails>) => {
    if (isInitialising) return
    const { path } = e.detail
    navigate(path)
  }

  const {
    fetchData: postRestoreBrand,
    isLoading: isPostRestoreBrandLoading,
    url: postRestoreBrandUrl,
    setUrl: setPostRestoreBrandUrl,
  } = useApi<RestoreCreateData>('')

  const currentVersion = useMemo(() => {
    const version = parseInt(searchParams.get('version') || 'NaN') || getBrandData?.version || null
    setPostRestoreBrandUrl(createCsApiUrlFromEndpoint(`/brands/${brand_id}/restore/${version}`))
    return version
  }, [searchParams, getBrandData])

  const {
    fetchData: getBrandVersion,
    data: getBrandVersionData,
    isLoading: isGetBrandVersionLoading,
    error: getBrandVersionError,
  } = useApi<BrandsUpdateData>(
    createCsApiUrlFromEndpoint(`brands/${brand_id}/version/${currentVersion}`),
    {},
    0,
    'json',
    true,
  )

  const isCurrentVersionTheLatest = currentVersion !== null && currentVersion === getBrandData?.version

  const brandData = useMemo(() => {
    return isCurrentVersionTheLatest ? (getBrandData as BrandsDetailData) : (getBrandVersionData as BrandsUpdateData)
  }, [isGetBrandLoading, isGetBrandVersionLoading, getBrandData, getBrandVersionData, isCurrentVersionTheLatest])

  const menuItems = useMemo(() => {
    const items = []

    if (canDeleteBrand) {
      items.push({
        label: 'Delete brand',
        onClick: () => setIsDeleteModalOpen(true),
      })
    }

    if (!isCurrentVersionTheLatest && canPostRestoreBrand) {
      items.unshift({
        label: 'Restore',
        onClick: () => setIsRestoreModalOpen(true),
      })
    }

    return items
  }, [isCurrentVersionTheLatest, canPostRestoreBrand])

  useEffect(() => {
    if (currentVersion !== null) {
      getBrandVersion(`brands/${brand_id}/version/${currentVersion}`)
        .then(() => {
          if (isCurrentVersionTheLatest) {
            const newSearchParams = new URLSearchParams(searchParams)
            newSearchParams.delete('version')
            setSearchParams(newSearchParams)
          }
        })
        .catch(err => console.error(err))
    }
  }, [currentVersion])

  // initial request
  useEffect(() => {
    if (!getBrandData && !isGetBrandLoading) {
      getBrand(getBrandUrl).catch(err => console.error(err))
    }
  }, [])

  const handleSubmitForm = (brandData: BrandsCreatePayload) => {
    // this function will update the client data with a PUT
    const updateBrandData = async () => {
      try {
        // PUT new brand data
        const putResponse = await putBrand(putBrandUrl, {
          method: 'PUT',
          body: JSON.stringify(brandData),
          headers: { [PDP_GENERATOR_API_HEADERS.CLIENT_IDS_HEADER]: brandData.client_id as string },
        })
        if (Reflect.has(putResponse, 'errors')) {
          return putResponse
        }
        // GET updated data from the API
        await getBrand(getBrandUrl)

        // reset form
        setIsFormActive(false)

        // show banner
        resetBanner()
        setToastDetails({
          message: `Brand rules successfully edited, creating version ${(putResponse?.version as number) || NaN} edited from version ${(putResponse?.update_info?.from_version as number) || NaN}`,
          type: 'success',
        })
      } catch (error) {
        const err = error as Error
        throw err
      }
    }

    // call the actual update brand data function
    return new Promise((resolve, reject) => {
      updateBrandData()
        .then(result => {
          resolve(result)
        })
        .catch(error => {
          reject(error.message)
        })
    })
  }

  const isInitialising =
    isGetBrandLoading ||
    isGetPermissionsLoading ||
    (!getBrandData && !getBrandError) ||
    isGetBrandVersionLoading ||
    (!getBrandVersionData && !getBrandVersionError) ||
    !brandData

  if (
    !isInitialising &&
    (!canGetBrand ||
      (!isCurrentVersionTheLatest && !canGetBrandVersion) ||
      (getBrandError && !isGetBrandLoading) ||
      (getBrandVersionError && !isGetBrandVersionLoading))
  )
    return <NotFound404Page />

  const resetBanners = () => {
    if (BANNER_KEYS.RESTORE_SUCCESS.BRAND_VERSION === activeBannerId && !!toastDetailsContext) {
      resetBanner()
    }
    if (!!toastDetails) {
      setToastDetails(undefined)
    }
  }

  const handleDelete = async ({ comment }: OnSubmitGenericModalProps) => {
    const body: ArchiveCreatePayload = {
      update_info: {
        user_name: userName,
        comment,
      },
    }

    try {
      await deleteBrand(deleteBrandUrl, {
        method: 'POST',
        body: JSON.stringify(body),
      })

      updatePreservedFilterSelection(
        FILTER_IDS.CLIENT_AND_BRAND_PROJECT,
        currentFilterSelection[FILTER_IDS.CLIENT_AND_BRAND_PROJECT]?.filter(({ filterId }) => filterId !== 'brands') ||
          [],
      )

      // reset form
      await fetchClientData()

      // redirect to the brands page
      setIsFormActive(false)

      // show banner in client page
      setActiveBannerId(client_id)
      setToastDetailsContext({
        message: 'Brand successfully deleted',
        type: 'success',
      })
      navigate(`/settings/client/${client_id}`)
    } catch (err) {
      const error = err as FEApiError
      console.error(error, brand_id)
      setToastDetails({
        message: 'Error: Unable to delete brand',
        type: 'error',
      })
    } finally {
      setIsDeleteModalOpen(false)
    }
  }

  const handleRestore = async ({ comment }: OnSubmitGenericModalProps) => {
    const body: RestoreCreateBody = {
      update_info: {
        user_name: userName,
        comment: comment,
      },
    }

    try {
      const res = await postRestoreBrand(postRestoreBrandUrl, {
        method: 'POST',
        body: JSON.stringify(body),
      })

      await getBrand(getBrandUrl)

      setToastDetails({
        message: `Brand rules successfully restored creating version ${res?.version as number} restored from version ${res?.update_info?.from_version as number}`,
        type: 'success',
      })

      const newSearchParams = new URLSearchParams(searchParams)
      newSearchParams.delete('version')
      setSearchParams(newSearchParams)

      await fetchClientData()

      navigate(location.pathname)

      // Reset the preserved brand filters in app context
      updatePreservedFilterSelection(
        FILTER_IDS.CLIENT_AND_BRAND_PROJECT,
        currentFilterSelection[FILTER_IDS.CLIENT_AND_BRAND_PROJECT]?.filter(({ filterId }) => filterId !== 'brands') ||
          [],
      )
    } catch (err) {
      setToastDetails({
        message: 'Error restoring brand rules',
        type: 'error',
      })
    } finally {
      setIsRestoreModalOpen(false)
    }
  }

  return (
    <div className="dark-bg-page">
      <div className={clsx('light-header', styles.header)}>
        <div className={navigationHeaderStyles.navigationHeader__container}>
          <WppBreadcrumb items={breadCrumbItems} middleTruncation onWppChange={handleRouteChange} />

          <Banner
            text={toastDetailsContext?.message || toastDetails?.message}
            type={toastDetailsContext?.type || toastDetails?.type}
            onBannerClick={toastDetailsContext?.onBannerClick || toastDetails?.onBannerClick}
            shouldBannerShow={(activeBannerId === brand_id && !!toastDetailsContext) || !!toastDetails}
            onClose={resetBanners}
          />

          {/* permanent banner for when editing current version */}
          <Banner
            text="You are editing the current version. Any saved changes will create a new version."
            type="info"
            shouldBannerShow={isFormActive && isCurrentVersionTheLatest && !toastDetails && !toastDetailsContext}
          />

          {/* permanent banner when viewing older version */}
          <Banner
            text="You are viewing an older version of the brand rules."
            type="info"
            shouldBannerShow={!isCurrentVersionTheLatest && !toastDetails && !toastDetailsContext}
          />
        </div>

        <PageHeader
          isLoading={isInitialising}
          tags={[]}
          createdDetails={`${getBrandData?.updated_at === getBrandData?.created_at ? 'Created' : 'Updated'}: ${getFormattedDate(getBrandData?.updated_at)} by ${getBrandData?.update_info?.user_name || unknown}`}
          pageTitle={getBrandData?.brand_name!}
          menuItems={menuItems}
          beforeDetails={
            <>
              {canGetBrandVersion && (
                <VersionHistoryList<BrandsDetailData>
                  historyArray={(getBrandData as BrandsDetailData)?.version_history}
                  currentVersion={brandData}
                  onVersionClickCB={() => {
                    // reset banner when clicking on a version
                    resetBanners()

                    const hasCurrentVersionLoadedInHistory = (getBrandData as BrandsDetailData)?.version_history?.find(
                      ({ version }) => version === currentVersion,
                    )
                    if (!hasCurrentVersionLoadedInHistory) getBrand(getBrandUrl).catch(err => console.error(err))
                    setIsFormActive(false)
                  }}
                />
              )}
            </>
          }
        />
      </div>

      <div className={styles.wrapper}>
        <div className={clsx(styles.actions, { [styles.space_top]: isInitialising })}>
          {!isInitialising && canPutBrand && (
            <>
              {!isFormActive ? (
                <WppButton
                  type="submit"
                  variant="primary"
                  data-testid="edit-changes-cta"
                  onClick={() => setIsFormActive(true)}
                >
                  Edit
                </WppButton>
              ) : (
                <>
                  <WppButton variant="secondary" data-testid="save-changes-cta" onClick={() => setIsFormActive(false)}>
                    Cancel
                  </WppButton>
                  <WppButton type="submit" form="brand-form" variant="primary" data-testid="save-changes-cta">
                    Save
                  </WppButton>
                </>
              )}
            </>
          )}
        </div>

        <div className={styles.formContainer}>
          {isInitialising ? (
            <FormSkeleton />
          ) : (
            <CreateAndEditNewBrandForm
              onSubmitForm={handleSubmitForm}
              client_id={client_id as string}
              initialData={
                isCurrentVersionTheLatest
                  ? (getBrandData as BrandsDetailData)
                  : (getBrandVersionData as BrandsUpdateData)
              }
              isFormActive={isFormActive}
              setToastDetails={setToastDetails}
              redirectToNewBrandView={isCurrentVersionTheLatest}
              isLoading={isPutBrandLoading}
            />
          )}
        </div>
      </div>

      {isRestoreModalOpen && brandData && !isCurrentVersionTheLatest && (
        <GenericModal
          shouldModalOpen={isRestoreModalOpen}
          modalTitle="Restore version"
          onCancel={() => setIsRestoreModalOpen(false)}
          submitButtonText="Restore"
          isApiLoading={isPostRestoreBrandLoading || isClientApiLoading}
          fieldsConfig={{
            hasCommentField: true,
            isCommentRequiredField: false,
          }}
          onSubmit={handleRestore}
        >
          <WppTypography tag="p" type="s-body" data-testid="modal-disclaimer">
            This action will create a new version. <br />
            After you save version this action cannot be undone.
          </WppTypography>
        </GenericModal>
      )}

      {isDeleteModalOpen && (
        <GenericModal
          shouldModalOpen={isDeleteModalOpen}
          modalTitle="Delete Brand"
          onCancel={() => setIsDeleteModalOpen(false)}
          submitButtonText="Delete"
          isApiLoading={isDeleteBrandLoading || isClientApiLoading}
          fieldsConfig={{
            hasCommentField: true,
            isCommentRequiredField: false,
          }}
          onSubmit={handleDelete}
        >
          <WppTypography tag="p" type="s-body" data-testid="modal-disclaimer">
            This action will impact {getBrandData?.metadata?.project_count} projects and{' '}
            {getBrandData?.metadata?.job_count} tasks. <br />
            After you delete a brand this action cannot be undone.
          </WppTypography>
        </GenericModal>
      )}
    </div>
  )
}
